import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint } from '@/hooks/ui/useBreakpoint';
import { useError } from '@/contexts/ErrorContext';

export function PasswordChangeModal() {
  const { changePassword } = useAuth();
  const { isMobile } = useBreakpoint();
  const { showError: showErrorToast } = useError();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validatePassword = (): string | null => {
    if (newPassword.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (newPassword !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    const { error } = await changePassword(newPassword);
    setLoading(false);

    if (error) {
      const message = error.message || 'Failed to change password';
      setError(message);
      showErrorToast(error, { message }); // Toast notification
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--color-border-solid)',
    background: 'var(--color-bg-input)',
    color: 'var(--color-text-primary)',
    fontSize: '16px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    color: 'var(--color-text-secondary)',
    fontSize: '14px',
    fontWeight: 500,
  };

  const content = (
    <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>🔐</div>
        <h2 style={{ margin: 0, color: 'var(--color-text-primary)', fontSize: '20px' }}>
          Change Your Password
        </h2>
        <p style={{ margin: '8px 0 0', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
          Please set a new password to continue
        </p>
      </div>

      <div>
        <label style={labelStyle}>New Password</label>
        <input
          type="password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          style={inputStyle}
          placeholder="Enter new password"
          autoFocus
          disabled={loading}
        />
      </div>

      <div>
        <label style={labelStyle}>Confirm Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          style={inputStyle}
          placeholder="Confirm new password"
          disabled={loading}
        />
      </div>

      {error && (
        <div style={{
          padding: '10px 12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid var(--color-error)',
          borderRadius: '8px',
          color: 'var(--color-error)',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !newPassword || !confirmPassword}
        style={{
          width: '100%',
          padding: '14px',
          borderRadius: '8px',
          border: 'none',
          background: loading || !newPassword || !confirmPassword
            ? 'var(--color-bg-tertiary)'
            : 'var(--color-accent)',
          color: loading || !newPassword || !confirmPassword
            ? 'var(--color-text-secondary)'
            : 'white',
          fontSize: '16px',
          fontWeight: 600,
          cursor: loading || !newPassword || !confirmPassword ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? 'Changing Password...' : 'Change Password'}
      </button>
    </form>
  );

  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-bg-primary)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        paddingTop: '60px',
      }}>
        {content}
      </div>
    );
  }

  return (
    <>
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 1000,
      }} />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--color-bg-secondary)',
        borderRadius: '12px',
        padding: '32px',
        zIndex: 1001,
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      }}>
        {content}
      </div>
    </>
  );
}
